import React, { useEffect, useRef, useState } from "react";
import ChatBubbleOutline from "@material-ui/icons/ChatBubbleOutline";
import { getCampaigns } from "apis/campaign.api";
import {
  createChatThread,
  getChatThreads,
  getChatThreadMessages,
  createChatThreadMessage,
  MULTI_CAMPAIGN_CHAT_KIND,
} from "apis/chat.api";
import styles from "./aica-chat.page.module.scss";

const listCampaigns = (data) => (Array.isArray(data) ? data : data?.campaigns || []);
const codesFor = (thread) => (Array.isArray(thread?.campaignCodes) ? thread.campaignCodes : []);
const idFor = (thread) => thread.insightChatThreadId;
const totalFor = (campaigns) =>
  campaigns.reduce((total, campaign) => total + campaign.respondents, 0);
const sameCode = (first, second) => String(first).toLowerCase() === String(second).toLowerCase();
const threadTotal = (thread, campaigns) => {
  const count = Number(thread?.totalRespondents);
  return thread?.totalRespondents != null && Number.isFinite(count) ? count : totalFor(campaigns);
};
const messageFor = (message) => ({
  id: message.insightChatMessageId,
  role: message.role,
  content: message.content || "",
  comparison: message.comparison,
  appliedFilters: message.appliedFilters,
});
const startersFor = (multiple) =>
  multiple
    ? [
        "Compare the top concerns across these campaigns",
        "How does sentiment differ between them?",
        "Compare age and gender mix",
      ]
    : [
        "What are the top concerns?",
        "Summarise sentiment in this campaign",
        "Show the age and gender mix",
      ];

export const filterCampaigns = (campaigns, search, onlyRecordings) => {
  const term = search.trim().toLowerCase();
  return campaigns
    .filter(
      (campaign) =>
        (!onlyRecordings || campaign.respondents > 0) &&
        [campaign.code, campaign.name, campaign.location].some((value) =>
          String(value).toLowerCase().includes(term),
        ),
    )
    .sort(
      (first, second) =>
        second.respondents - first.respondents || first.code.localeCompare(second.code),
    );
};

const readCampaigns = async () => {
  const rows = listCampaigns(await getCampaigns());
  return rows.map((campaign) => {
    const code = campaign.campaignCode || campaign.code;
    const count = Number(campaign.respondentCount);
    if (!code || !Number.isFinite(count) || count < 0) {
      throw new Error("Campaign respondent totals are unavailable. Please try again.");
    }
    return {
      code,
      name: campaign.description || campaign.name || code,
      location: campaign.location || "Unknown location",
      date: String(campaign.campaignDate || campaign.date || "").split("T")[0],
      respondents: count,
    };
  });
};

const chatErrorMessage = (failure) => {
  const code = failure?.data?.errorCode || failure?.data?.code;
  if (code === "IC1006")
    return "Multi-campaign chat is temporarily unavailable. Please retry shortly.";
  if (code === "IC1002")
    return "Open-ended answers are still being grouped into themes. Please retry shortly.";
  if (code === "RP1001")
    return "A campaign was deleted or a campaign code does not exist. Update your selection and try again.";
  return failure?.message || "Could not complete your request. Please try again.";
};

const valueText = (value) => {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (typeof value === "object" && (value.respondentCount != null || value.percentOfBase != null)) {
    const count = value.respondentCount;
    const percent = value.percentOfBase;
    const people =
      count != null ? `${Number(count).toLocaleString()} ${count === 1 ? "person" : "people"}` : "";
    const share = percent != null ? `${Number(percent).toFixed(1)}%` : "";
    return people && share ? `${people} (${share})` : people || share;
  }
  return JSON.stringify(value);
};

const cellFor = (row, campaign, index) => {
  const match = (row.values || []).find(
    (value) => value && typeof value === "object" && value.campaignCode === campaign.campaignCode,
  );
  return match ?? row.values?.[index];
};

const Comparison = ({ comparison, appliedFilters = [] }) => {
  if (!comparison) return null;
  const campaigns = comparison.campaigns || [];
  const columns = comparison.table?.columns || [];
  const rows = comparison.table?.rows || [];
  const hasCombined = rows.some((row) => Object.prototype.hasOwnProperty.call(row, "combined"));
  return (
    <>
      <div className={styles.tableScroll}>
        <table>
          <thead>
            <tr>
              {columns.map((column, index) => (
                <th key={index}>{valueText(column)}</th>
              ))}
              {campaigns.map((campaign, index) => (
                <th key={`${campaign.campaignCode}-${index}`}>
                  {campaign.campaignCode}
                  <small>
                    {campaign.status} · N={campaign.baseN ?? "—"}/{campaign.totalN ?? "—"}
                  </small>
                </th>
              ))}
              {hasCombined && <th>Overall</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {(row.keys || []).map((key, index) => (
                  <td key={index}>{valueText(key)}</td>
                ))}
                {campaigns.map((campaign, index) => (
                  <td key={`${campaign.campaignCode}-${index}`}>
                    {valueText(cellFor(row, campaign, index)) || "—"}
                  </td>
                ))}
                {hasCombined && <td>{valueText(row.combined) || "—"}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {campaigns.some((campaign) => campaign.smallBase) && (
        <p className={styles.warning}>
          Results may not be reliable:{" "}
          {campaigns
            .filter((campaign) => campaign.smallBase)
            .map((campaign) => campaign.campaignCode)
            .join(", ")}{" "}
          contributed fewer than 10 respondents.
        </p>
      )}
      {!!appliedFilters.length && (
        <p className={styles.base}>
          Filters:{" "}
          {appliedFilters
            .map((filter) => {
              const labels = filter.optionLabels || [];
              return `${filter.question?.kind || "Question"}${labels.length ? `: ${labels.join(", ")}` : ""}`;
            })
            .join(" · ")}
        </p>
      )}
    </>
  );
};

const cellsFor = (line) =>
  line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((cell) => cell.trim());

const Answer = ({ content }) => {
  const lines = String(content).split("\n");
  const blocks = [];
  for (let index = 0; index < lines.length; index += 1) {
    if (lines[index].includes("|") && /^\s*\|?\s*:?-{3,}/.test(lines[index + 1] || "")) {
      const headers = cellsFor(lines[index]);
      const rows = [];
      index += 2;
      while (index < lines.length && lines[index].includes("|")) {
        rows.push(cellsFor(lines[index]));
        index += 1;
      }
      index -= 1;
      blocks.push(
        <div className={styles.tableScroll} key={index}>
          <table>
            <thead>
              <tr>
                {headers.map((header, column) => (
                  <th key={column}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {headers.map((header, column) => (
                    <td key={column}>{row[column] || ""}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
    } else {
      blocks.push(
        <div className={styles.answerLine} key={index}>
          {lines[index] || "\u00a0"}
        </div>,
      );
    }
  }
  return blocks;
};

const AicaChatPage = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [onlyRecordings, setOnlyRecordings] = useState(true);
  const [conversations, setConversations] = useState({});
  const [pending, setPending] = useState({});
  const [loadingThread, setLoadingThread] = useState(null);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const [accessReady, setAccessReady] = useState(true);
  const sequence = useRef(0);
  const mounted = useRef(true);
  const pendingIds = useRef(new Set());
  const bottom = useRef(null);

  useEffect(() => {
    mounted.current = true;
    const initialize = async () => {
      try {
        const [available, result] = await Promise.all([
          readCampaigns(),
          getChatThreads({ kind: MULTI_CAMPAIGN_CHAT_KIND, page: 1, perPage: 100 }),
        ]);
        if (!mounted.current) return;
        setCampaigns(available);
        setThreads(
          (result?.threads || [])
            .filter((thread) => !thread.kind || thread.kind === MULTI_CAMPAIGN_CHAT_KIND)
            .sort((first, second) => new Date(second.createdAt) - new Date(first.createdAt)),
        );
      } catch {
        if (mounted.current) setError("Could not load Aica Chat. Please reload to try again.");
      } finally {
        if (mounted.current) setLoading(false);
      }
    };
    initialize();
    const hide = () => {
      if (document.hidden) setAccessReady(false);
    };
    const revalidate = async () => {
      if (document.hidden) return;
      setAccessReady(false);
      try {
        const available = await readCampaigns();
        if (mounted.current) {
          setCampaigns(available);
          setAccessReady(true);
        }
      } catch {
        if (mounted.current) {
          setConversations({});
          setError("Unable to verify campaign access. Please reload.");
        }
      }
    };
    const visibility = () => (document.hidden ? hide() : revalidate());
    window.addEventListener("focus", revalidate);
    document.addEventListener("visibilitychange", visibility);
    return () => {
      mounted.current = false;
      sequence.current += 1;
      window.removeEventListener("focus", revalidate);
      document.removeEventListener("visibilitychange", visibility);
    };
  }, []);

  const active = threads.find((thread) => idFor(thread) === activeId);
  const scoped = codesFor(active)
    .map((code) => campaigns.find((campaign) => sameCode(campaign.code, code)))
    .filter(Boolean);
  const allowed =
    active && codesFor(active).length > 0 && scoped.length === codesFor(active).length;
  const selectedCampaigns = campaigns.filter((campaign) =>
    selected.some((code) => sameCode(code, campaign.code)),
  );
  const activeRespondents = threadTotal(active, scoped);
  const messages = conversations[activeId] || [];
  const activePending = pending[activeId];

  useEffect(() => {
    bottom.current?.scrollIntoView?.({ block: "nearest" });
  }, [activeId, messages.length, activePending]);

  const verify = async (codes) => {
    const available = await readCampaigns();
    if (!mounted.current) throw new Error("Page closed.");
    setCampaigns(available);
    if (
      !codes.length ||
      codes.some((code) => !available.some((campaign) => sameCode(campaign.code, code)))
    ) {
      setConversations({});
      throw new Error(
        "Access to one or more campaigns is no longer available. Start a new thread.",
      );
    }
    return available.filter((campaign) => codes.some((code) => sameCode(code, campaign.code)));
  };

  const startThread = async () => {
    if (!selected.length || creating) return;
    setCreating(true);
    setError("");
    const request = ++sequence.current;
    const codes = [...selected];
    try {
      await verify(codes);
      const thread = await createChatThread({
        campaignCodes: codes,
        kind: MULTI_CAMPAIGN_CHAT_KIND,
      });
      if (
        !idFor(thread) ||
        thread.kind !== MULTI_CAMPAIGN_CHAT_KIND ||
        codesFor(thread).length !== codes.length ||
        codes.some((code) => !codesFor(thread).some((threadCode) => sameCode(code, threadCode)))
      ) {
        throw new Error(
          "The server did not save the selected campaigns. Multi-campaign chat support is required.",
        );
      }
      if (!mounted.current) return;
      setThreads((previous) => [thread, ...previous]);
      if (request === sequence.current) {
        setActiveId(idFor(thread));
        setInput("");
      }
      setSelected([]);
    } catch (failure) {
      if (mounted.current) setError(chatErrorMessage(failure));
    } finally {
      if (mounted.current) setCreating(false);
    }
  };

  const openThread = async (thread) => {
    const threadId = idFor(thread);
    const request = ++sequence.current;
    setActiveId(threadId);
    setInput("");
    setError("");
    setLoadingThread(threadId);
    try {
      await verify(codesFor(thread));
      if (pendingIds.current.has(threadId)) return;
      const result = await getChatThreadMessages({
        insightChatThreadId: threadId,
        page: 1,
        perPage: 100,
      });
      if (!mounted.current || request !== sequence.current || pendingIds.current.has(threadId))
        return;
      setConversations((previous) => ({
        ...previous,
        [threadId]: (result?.messages || []).map(messageFor),
      }));
      const firstQuestion = (result?.messages || []).find((message) => message.role === "user");
      if (firstQuestion)
        setThreads((previous) =>
          previous.map((item) =>
            idFor(item) === threadId ? { ...item, title: firstQuestion.content } : item,
          ),
        );
    } catch (failure) {
      if (mounted.current && request === sequence.current) setError(chatErrorMessage(failure));
    } finally {
      if (mounted.current && request === sequence.current) setLoadingThread(null);
    }
  };

  const send = async (suggestion) => {
    const prompt = (suggestion || input).trim();
    if (
      !prompt ||
      !active ||
      !allowed ||
      !accessReady ||
      loadingThread === activeId ||
      pendingIds.current.has(activeId)
    )
      return;
    const threadId = activeId;
    const codes = [...codesFor(active)];
    pendingIds.current.add(threadId);
    setPending((previous) => ({ ...previous, [threadId]: true }));
    setInput("");
    setError("");
    const append = (message) => {
      if (mounted.current)
        setConversations((previous) => ({
          ...previous,
          [threadId]: [...(previous[threadId] || []), message],
        }));
    };
    try {
      await verify(codes);
      append({ role: "user", content: prompt });
      const hasQuestion = messages.some((message) => message.role === "user");
      if (!hasQuestion)
        setThreads((previous) =>
          previous.map((thread) =>
            idFor(thread) === threadId ? { ...thread, title: prompt } : thread,
          ),
        );
      const result = await createChatThreadMessage({ insightChatThreadId: threadId, prompt });
      await verify(codes);
      if (!result?.assistantMessage?.content && !result?.assistantMessage?.comparison)
        throw new Error("No reply was returned. Please try again.");
      append(messageFor(result.assistantMessage));
    } catch (failure) {
      append({
        role: "error",
        content: chatErrorMessage(failure),
      });
    } finally {
      pendingIds.current.delete(threadId);
      if (mounted.current) setPending((previous) => ({ ...previous, [threadId]: false }));
    }
  };

  const toggle = (code) =>
    setSelected((previous) => {
      if (previous.some((value) => sameCode(value, code)))
        return previous.filter((value) => !sameCode(value, code));
      return previous.length >= 10 ? previous : [...previous, code];
    });
  const visible = filterCampaigns(campaigns, search, onlyRecordings);

  return (
    <main className={styles.page}>
      <header className={styles.header}>
        <h1>
          <ChatBubbleOutline /> Aica Chat
        </h1>
        <p>Pick the campaigns for a thread, then ask Aica to compare or combine them.</p>
      </header>
      {error && (
        <p role="alert" className={styles.error}>
          {error}
        </p>
      )}
      <div className={styles.workspace}>
        <aside className={styles.threads} aria-label="Threads">
          <div className={styles.threadHeading}>
            <div>
              <h2>Threads</h2>
              <p>Each thread has its own campaigns</p>
            </div>
            <button
              onClick={() => {
                sequence.current += 1;
                setActiveId(null);
                setLoadingThread(null);
                setSelected([]);
                setInput("");
                setError("");
              }}
            >
              New
            </button>
          </div>
          {loading ? (
            <p role="status">Loading threads...</p>
          ) : (
            !threads.length && <p>No threads yet. Pick campaigns to start one.</p>
          )}
          {threads.map((thread) => (
            <button
              key={idFor(thread)}
              className={`${styles.thread} ${activeId === idFor(thread) ? styles.selected : ""}`}
              onClick={() => openThread(thread)}
              aria-pressed={activeId === idFor(thread)}
            >
              <strong>{thread.title || codesFor(thread).join(" + ")}</strong>
              <span className={styles.codes}>
                {codesFor(thread).map((code) => (
                  <span key={code}>{code}</span>
                ))}
              </span>
              <time dateTime={thread.createdAt}>
                {thread.createdAt ? new Date(thread.createdAt).toLocaleString() : ""}
              </time>
            </button>
          ))}
        </aside>
        {!active ? (
          <section className={styles.picker} aria-label="Choose campaigns">
            <h2>Which campaigns should this thread look at?</h2>
            <p>
              Choose one or more. They stay fixed for the whole thread so answers remain comparable.
            </p>
            <div className={styles.filters}>
              <input
                aria-label="Search campaigns"
                placeholder="Search by code, name or location..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
              <label>
                <input
                  type="checkbox"
                  checked={onlyRecordings}
                  onChange={(event) => setOnlyRecordings(event.target.checked)}
                />{" "}
                Only campaigns with recordings
              </label>
            </div>
            <div className={styles.campaignList}>
              {loading ? (
                <p role="status">Loading campaigns...</p>
              ) : visible.length === 0 ? (
                <p>
                  {search.trim()
                    ? `No campaign matches "${search.trim()}"`
                    : "No campaigns available."}
                </p>
              ) : (
                visible.map((campaign) => (
                  <label
                    key={campaign.code}
                    className={`${styles.campaign} ${selected.some((code) => sameCode(code, campaign.code)) ? styles.selected : ""} ${campaign.respondents === 0 ? styles.faded : ""}`}
                  >
                    <input
                      type="checkbox"
                      aria-label={`Select ${campaign.code}`}
                      checked={selected.some((code) => sameCode(code, campaign.code))}
                      disabled={
                        selected.length >= 10 &&
                        !selected.some((code) => sameCode(code, campaign.code))
                      }
                      onChange={() => toggle(campaign.code)}
                    />
                    <strong>{campaign.code}</strong>
                    <span className={styles.campaignName}>
                      {campaign.name}
                      <small>
                        {campaign.location} &middot; {campaign.date || "No date"}
                      </small>
                    </span>
                    <small>{campaign.respondents} respondents</small>
                  </label>
                ))
              )}
            </div>
            <footer className={styles.pickerFooter}>
              <span aria-live="polite">
                {selected.length
                  ? `${selected.length} campaign${selected.length === 1 ? "" : "s"} \u00b7 ${totalFor(selectedCampaigns)} respondents`
                  : "No campaigns selected"}
              </span>
              <div>
                <button className={styles.secondary} onClick={() => setSelected([])}>
                  Clear
                </button>
                <button disabled={!selected.length || creating || loading} onClick={startThread}>
                  {creating
                    ? "Starting..."
                    : selected.length > 1
                      ? `Start thread with ${selected.length} campaigns`
                      : "Start thread"}
                </button>
              </div>
            </footer>
          </section>
        ) : (
          <section className={styles.conversation} aria-label="Conversation">
            {!accessReady ? (
              <p role="status">Verifying campaign access...</p>
            ) : !allowed ? (
              <p role="alert">
                Access to one or more campaigns is no longer available. Start a new thread.
              </p>
            ) : (
              <>
                <div className={styles.scope}>
                  <span>Campaigns</span>
                  <span className={styles.codes}>
                    {codesFor(active).map((code) => (
                      <span key={code}>{code}</span>
                    ))}
                  </span>
                  <span>{activeRespondents} respondents</span>
                </div>
                <div
                  className={styles.messages}
                  role="log"
                  aria-label="Messages"
                  aria-live="polite"
                >
                  <div className={styles.message}>
                    <p>
                      Hi! I am Aica. This thread looks at{" "}
                      <strong>{codesFor(active).join(", ")}</strong> ({activeRespondents}{" "}
                      respondents).{" "}
                      {activeRespondents === 0
                        ? "There are no processed recordings, so there is nothing to compare yet."
                        : "What would you like to explore?"}
                    </p>
                    {!messages.some((message) => message.role === "user") && (
                      <div className={styles.starters}>
                        {startersFor(scoped.length > 1).map((starter) => (
                          <button
                            className={styles.secondary}
                            key={starter}
                            disabled={pending[activeId] || loadingThread === activeId}
                            onClick={() => send(starter)}
                          >
                            {starter}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {loadingThread === activeId ? (
                    <p role="status">Loading conversation...</p>
                  ) : (
                    messages.map((message, index) => {
                      return (
                        <article
                          key={message.id || index}
                          className={`${styles.message} ${message.role === "user" ? styles.user : ""}`}
                        >
                          <Answer content={message.content} />
                          {message.role === "assistant" && (
                            <Comparison
                              comparison={message.comparison}
                              appliedFilters={message.appliedFilters}
                            />
                          )}
                        </article>
                      );
                    })
                  )}
                  {pending[activeId] && <p role="status">Aica is thinking...</p>}
                  <div ref={bottom} />
                </div>
                <form
                  className={styles.composer}
                  onSubmit={(event) => {
                    event.preventDefault();
                    send();
                  }}
                >
                  <input
                    aria-label="Message Aica"
                    placeholder="Ask Aica to compare these campaigns..."
                    value={input}
                    onChange={(event) => setInput(event.target.value)}
                    disabled={pending[activeId] || loadingThread === activeId}
                  />
                  <button
                    disabled={!input.trim() || pending[activeId] || loadingThread === activeId}
                  >
                    Send
                  </button>
                </form>
              </>
            )}
          </section>
        )}
      </div>
    </main>
  );
};

export default AicaChatPage;
